/**
 * Mongo Atlas service – all requests go through apiClient to platform-data backend.
 * Uses platform-data endpoints for Atlas cluster, database, collection, and index usage data.
 */

import { apiClient } from '../api/apiClient'
import { logger } from '../../utils/logger'

export type MongoEnv = 'staging' | 'production'

// Project IDs for environments
const PROJECT_IDS: Record<MongoEnv, string> = {
  staging: '619b8370e9cc571b1d490117',
  production: '60803ad4b9f71f2ec1288526'
}

// Base URLs for environments
const BASE_URLS: Record<MongoEnv, string> = {
  staging: 'https://dev.services.leadconnectorhq.com',
  production: 'https://services.leadconnectorhq.com'
}

export interface MongoCluster {
  id: string
  name: string
  srv?: string
}

export interface MongoCollectionItem {
  database: string
  name: string
  type?: string
}

export interface ClustersResponse {
  clusters: MongoCluster[]
}

export interface CollectionsResponse {
  collections: MongoCollectionItem[]
}

export interface DatabasesResponse {
  databases: string[]
}

// Index usage types based on actual API response
export interface IndexPerHostUsage {
  host: string
  ops: number
  since: string
}

export interface IndexUsage {
  name: string
  ops: number
  since: string
  perHost: IndexPerHostUsage[]
}

export interface MongoIndex {
  name: string
  key: Record<string, number | string>
  ttlSeconds?: number
  unique?: boolean
  sizeBytes?: number
  usage: IndexUsage
}

// Collection statistics from API
export interface CollectionStats {
  sizeBytes: number
  count: number
  avgObjSize: number
  storageSizeBytes: number
  totalIndexSizeBytes: number
}

// Helper to determine if an index is unique
// _id_ index is always unique by MongoDB design, even if API doesn't explicitly say so
export function isUniqueIndex(index: MongoIndex): boolean {
  return index.unique === true || index.name === '_id_'
}

// Helper to determine index type
export type IndexType = 'TTL' | 'Unique' | 'Compound' | 'Standard'

export function getIndexType(index: MongoIndex): IndexType {
  if (index.ttlSeconds !== undefined) return 'TTL'
  if (isUniqueIndex(index)) return 'Unique'
  if (Object.keys(index.key).length > 1) return 'Compound'
  return 'Standard'
}

export function getIndexTypeLabel(index: MongoIndex): string {
  const keyCount = Object.keys(index.key).length
  const types: string[] = []

  if (index.ttlSeconds !== undefined) {
    const ttlDisplay = formatTTLSeconds(index.ttlSeconds)
    types.push(`TTL ${ttlDisplay}`)
  }
  if (isUniqueIndex(index)) types.push('Unique')
  if (keyCount > 1) types.push(`Compound (${keyCount})`)

  return types.length > 0 ? types.join(' + ') : 'Standard'
}

function formatTTLSeconds(seconds: number): string {
  if (seconds >= 86400) return `${Math.round(seconds / 86400)}d`
  if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`
  return `${seconds}s`
}

export interface IndexUsageResponse {
  collectionStats: CollectionStats
  indexes: MongoIndex[]
}

// Cache types
export interface CacheStats {
  clusters: { size: number; keys: string[] }
  databases: { size: number; keys: string[] }
  collections: { size: number; keys: string[] }
  indexUsage: { size: number; keys: string[] }
  total: number
  traceId: string
}

export interface CacheClearResponse {
  databases: number
  collections: number
  indexUsage: number
  total: number
  clearedKeys: string[]
  traceId: string
}

function atlasUrl(path: string, env: MongoEnv, params?: Record<string, string>): string {
  const base = BASE_URLS[env]
  const url = new URL(path, base)
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))
  }
  return url.toString()
}

export const mongoService = {
  /**
   * Get the project ID for a given environment
   */
  getProjectId(env: MongoEnv): string {
    return PROJECT_IDS[env]
  },

  /**
   * Fetch cluster list for a project/environment.
   */
  async getClusters(env: MongoEnv = 'production'): Promise<ClustersResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = atlasUrl('/platform-data/atlas/clusters', env, { projectId })
      const response = await apiClient.get<Array<{ name: string; srv: string }>>(url)

      // Transform API response to our format
      const clusters: MongoCluster[] = (response || []).map((c) => ({
        id: c.name, // Use name as ID since API doesn't provide separate ID
        name: c.name,
        srv: c.srv
      }))

      return { clusters }
    } catch (error) {
      logger.error('Failed to fetch Mongo clusters', error)
      const message =
        error instanceof Error ? error.message : 'Unable to load clusters. Please try again later.'
      throw new Error(message)
    }
  },

  /**
   * Fetch databases for a cluster.
   */
  async getDatabases(
    clusterName: string,
    env: MongoEnv = 'production'
  ): Promise<DatabasesResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = atlasUrl(
        `/platform-data/atlas/clusters/${encodeURIComponent(clusterName)}/databases`,
        env,
        { projectId }
      )
      const response = await apiClient.get<string[]>(url)

      return { databases: response || [] }
    } catch (error) {
      logger.error('Failed to fetch databases', error)
      const message =
        error instanceof Error ? error.message : 'Failed to load databases. Please try again later.'
      throw new Error(message)
    }
  },

  /**
   * Fetch collections for a database in a cluster.
   */
  async getCollections(
    clusterName: string,
    dbName: string,
    env: MongoEnv = 'production'
  ): Promise<CollectionsResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = atlasUrl(
        `/platform-data/atlas/clusters/${encodeURIComponent(clusterName)}/databases/${encodeURIComponent(dbName)}/collections`,
        env,
        { projectId }
      )
      const response = await apiClient.get<string[]>(url)

      // Transform string array to collection items
      const collections: MongoCollectionItem[] = (response || []).map((name) => ({
        database: dbName,
        name
      }))

      return { collections }
    } catch (error) {
      logger.error('Failed to fetch collections', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load collections. Please try again later.'
      throw new Error(message)
    }
  },

  /**
   * Fetch index usage for a collection.
   */
  async getIndexUsage(
    clusterName: string,
    dbName: string,
    collectionName: string,
    env: MongoEnv = 'production'
  ): Promise<IndexUsageResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = atlasUrl('/platform-data/atlas/index-usage', env)

      const response = await apiClient.post<{
        collectionStats: CollectionStats
        indexes: MongoIndex[]
        traceId?: string
      }>(url, {
        projectId,
        clusterName,
        dbName,
        collectionName
      })

      return {
        collectionStats: response.collectionStats || {
          sizeBytes: 0,
          count: 0,
          avgObjSize: 0,
          storageSizeBytes: 0,
          totalIndexSizeBytes: 0
        },
        indexes: response.indexes || []
      }
    } catch (error) {
      logger.error('Failed to fetch index usage', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load index usage. Please try again later.'
      throw new Error(message)
    }
  },

  /**
   * Get cache statistics for Atlas data.
   */
  async getCacheStats(env: MongoEnv = 'production'): Promise<CacheStats> {
    try {
      const url = atlasUrl('/platform-data/atlas/cache', env)
      const response = await apiClient.get<CacheStats>(url)
      return response
    } catch (error) {
      logger.error('Failed to fetch cache stats', error)
      const message = error instanceof Error ? error.message : 'Failed to load cache statistics.'
      throw new Error(message)
    }
  },

  /**
   * Clear cache for a specific cluster.
   * Clears databases, collections, and indexUsage cache entries for the cluster.
   */
  async clearClusterCache(
    clusterName: string,
    env: MongoEnv = 'production'
  ): Promise<CacheClearResponse> {
    try {
      const projectId = PROJECT_IDS[env]
      const url = atlasUrl('/platform-data/atlas/cache/clear', env)

      const response = await apiClient.post<CacheClearResponse>(url, {
        projectId,
        clusterName
      })

      return response
    } catch (error) {
      logger.error('Failed to clear cache', error)
      const message =
        error instanceof Error ? error.message : 'Failed to clear cache. Please try again.'
      throw new Error(message)
    }
  }
}
